package com.exura.pages.planCreator.components

import androidx.compose.runtime.*
import com.exura.components.snackbar.SnackbarMessage
import com.exura.features.plandefinition.domain.builder.series.*
import com.exura.features.plandefinition.domain.model.plan.PlanType
import com.exura.features.plandefinition.domain.model.series.*
import com.exura.pages.planCreator.components.seriesViews.*
import com.exura.pages.planCreator.components.seriesViews.SimpleFloatingSeriesView
import com.exura.pages.planCreator.components.seriesViews.SimpleInstructionSeriesView
import com.exura.pages.planCreator.components.seriesViews.SimplePersistentSeriesView
import com.exura.pages.planCreator.model.SeriesTypeRegistry
import com.exura.ui.*
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.channels.Channel
import org.jetbrains.compose.web.css.px

@Composable
internal fun SeriesDetailsPanel(
    modifier: Modifier = Modifier,
    viewMode: ViewMode,
    selectedIndexedSeries: IndexedSeries?,
    selectedSeriesTypeForCreation: SeriesType?,
    snackbarEmitter: Channel<SnackbarMessage>,
    planType: PlanType,
    onSeriesSaved: (BaseSeries<*>) -> Unit,
    onSeriesAdded: (BaseSeries<*>) -> Unit,
) {
    val selectedSeries = selectedIndexedSeries?.series
    val innerMod = Modifier.fillMaxSize()
    Box(
        modifier = Modifier
            .fillMaxSize()
            .padding(
                top = 24.px,
                bottom = 24.px,
                left = COLLAPSED_SIZE,
                right = COLLAPSED_SIZE,
            )
            .overflow(Overflow.Auto)
            .then(modifier)
    ) {
        when {
            selectedSeries == null && selectedSeriesTypeForCreation == null -> {
                EmptyCanvasState(
                    modifier = innerMod,
                )
            }

            selectedSeries != null || selectedSeriesTypeForCreation != null -> {
                SeriesDetailsContent(
                    seriesType = selectedSeriesTypeForCreation,
                    series = selectedSeries,
                    planType = planType,
                    viewMode = viewMode,
                    modifier = innerMod,
                    addSeries = onSeriesAdded,
                    saveSeries = onSeriesSaved,
                )
            }
        }
    }
}

@Composable
private fun EmptyCanvasState(
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier,
        contentAlignment = Alignment.Center
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.px)
        ) {
            SpanText(
                text = "Wybierz serię z listy lub utwórz nową",
                modifier = Modifier
                    .classNames(TextStyles.medium18)
                    .color(ExuraColors.ARSENIC2)
                    .textAlign(TextAlign.Center)
            )

            SpanText(
                text = "Kliknij przycisk '+' aby dodać nową serię",
                modifier = Modifier
                    .classNames(TextStyles.normal14)
                    .color(ExuraColors.GRAY)
                    .textAlign(TextAlign.Center)
            )
        }
    }
}

@Composable
private fun SeriesDetailsContent(
    seriesType: SeriesType?,
    planType: PlanType,
    series: BaseSeries<*>?,
    viewMode: ViewMode,
    modifier: Modifier = Modifier,
    saveSeries: (BaseSeries<*>) -> Unit,
    addSeries: (BaseSeries<*>) -> Unit,
) {
    val isNewSeries = series == null
    val realSeriesType by remember(seriesType, series) { mutableStateOf(seriesType ?: SeriesTypeRegistry.seriesToType(series) ?: throw Exception()) }
    Box(
        modifier = Modifier.overflow(Overflow.Auto).then(modifier),
    ) {
        when (realSeriesType) {
            SeriesType.SIMPLE_INSTRUCTION -> {
                val instructionSeries = series as SimpleInstructionSeries?
                val builder = instructionSeries?.toBuilder() ?: SimpleInstructionSeriesBuilderNew()
                val onAction = {
                    val newSeries = builder.build()
                    if (isNewSeries) addSeries(newSeries) else saveSeries(newSeries)
                }
                SimpleInstructionSeriesView(
                    builder = builder,
                    isNewSeries = instructionSeries == null,
                    viewMode = viewMode,
                    onAction = onAction,
                    planType = planType,
                )
            }
            SeriesType.SIMPLE_FLOATING -> {
                val floatingSeries = series as SimpleFloatingSeries?
                val builder = floatingSeries?.toBuilder() ?: SimpleFloatingSeriesBuilderNew()
                val onAction = {
                    val newSeries = builder.build()
                    if (isNewSeries) addSeries(newSeries) else saveSeries(newSeries)
                }
                SimpleFloatingSeriesView(
                    builder = builder,
                    isNewSeries = floatingSeries == null,
                    viewMode = viewMode,
                    onAction = onAction,
                    planType = planType,
                )
            }
            SeriesType.SIMPLE_QUESTION -> {
                val questionSeries = series as SimpleQuestionSeries?
                val builder = questionSeries?.toBuilder() ?: SimpleQuestionSeriesBuilderNew()
                val onAction = {
                    val newSeries = builder.build()
                    if (isNewSeries) addSeries(newSeries) else saveSeries(newSeries)
                }
                SimpleQuestionSeriesView(
                    builder = builder,
                    isNewSeries = questionSeries == null,
                    viewMode = viewMode,
                    onAction = onAction,
                    planType = planType,
                )
            }
            SeriesType.SIMPLE_PERSISTENT -> {
                val persistentSeries = series as SimplePersistentSeries?
                val builder = persistentSeries?.toBuilder() ?: SimplePersistentSeriesBuilderNew()
                val onAction = {
                    val newSeries = builder.build()
                    if (isNewSeries) addSeries(newSeries) else saveSeries(newSeries)
                }
                SimplePersistentSeriesView(
                    builder = builder,
                    isNewSeries = persistentSeries == null,
                    viewMode = viewMode,
                    onAction = onAction,
                    planType = planType,
                )
            }
            else -> SeriesView(
                hasChanges = false,
                isValid = false,
                typeInfo = SeriesTypeRegistry.getTypeInfo(realSeriesType) ?: SeriesTypeRegistry.getTypeInfo(SeriesType.UNKNOWN)!!,
            ) {  }
        }
    }
}
